import React from 'react';
import { cardText } from 'components/ExportDesign/exportDesignResultCardData';
import {
  ChangePathPayload,
  changeNameOf,
  changePathViewOf,
  placeLabelOf,
} from 'components/ExportDesign/exportDesignChangeCardsData';

// --- The design changes as a card (UX slice S7, boards 06/07): from where it is to where it would land,
// each change as a before-and-after value with the control it clears and its trade-off, what is still left
// to check, or, when no change gets there, an honest no with what to do instead. Rendered from
// `data.change_path` in place of the markdown answer. The next moves are the run-options block under it
// (D-25). Only <div>s inside: the chat bubble's `p` margin rule outranks single classes.

// D-7 item 3 (pending compliance sign-off): static explainers, never a model answer, never a way around
// the control. An ITAR item's route is a Commodity Jurisdiction request; an EAR-listed item's a CCATS.
export const CJ_EXPLAINER =
  'A Commodity Jurisdiction (CJ) request asks the State Department’s Directorate of Defense Trade Controls ' +
  '(DDTC) to decide formally whether an item is covered by the ITAR (the US Munitions List) or by the EAR. You ' +
  'describe the item, how and why it was designed, and what it is used for. If you believe this product was not ' +
  'specially designed for a military use, a CJ is how to get a binding answer. It is usually prepared with export ' +
  'counsel or a trade professional, and a decision takes time.';
export const CCATS_EXPLAINER =
  'A commodity classification request (CCATS) asks the Commerce Department’s Bureau of Industry and Security ' +
  '(BIS) to confirm an item’s ECCN, or that it is EAR99. It is filed through BIS’s SNAP-R system with the ' +
  'item’s technical specifications. If you believe a different entry applies to this product, a CCATS is how ' +
  'to get a binding answer.';

interface ExportDesignChangePathCardProps {
  card: ChangePathPayload;
  /** Puts a sentence starter for an open spec in the composer (nothing is sent). */
  onAnswerAssumption?: (label: string) => void;
  disabled?: boolean;
}

const ExportDesignChangePathCard: React.FC<ExportDesignChangePathCardProps> = ({
  card,
  onAnswerAssumption,
  disabled,
}) => {
  const view = changePathViewOf(card);
  const d = card.delta;
  const explanation = cardText(d.explanation);
  const impact = cardText(d.functional_impact);
  const caveats = card.caveats.filter((c) => cardText(c.missing_spec));

  const stillToCheck = (d.residual_constraints.length > 0 || caveats.length > 0) && (
    <section>
      <div className='export-design-section-h'>Still to check after the change</div>
      <div className='export-design-assume'>
        {d.residual_constraints.map((r) => (
          <div key={`r-${r}`} className='export-design-assume-row'>
            <span>
              <b>Still binding:</b> {cardText(r)}
            </span>
          </div>
        ))}
        {caveats.map((c) => {
          const label = cardText(c.missing_spec);
          const name = label.charAt(0).toUpperCase() + label.slice(1);
          const assumed = cardText(c.assumed).replace(/\.$/, '');
          return (
            <div key={`c-${label}`} className='export-design-assume-row'>
              <span>
                <b>{name}.</b> {assumed ? `Assumed ${assumed}.` : 'Not given.'} {cardText(c.impact)}
              </span>
              {onAnswerAssumption && (
                <button
                  type='button'
                  className='standard-workflow-secondary-btn export-design-btn-sm'
                  onClick={() => onAnswerAssumption(name)}
                  disabled={disabled}
                >
                  Tell me
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className='export-design-result export-design-result-wide'>
      <article className='export-design-block' aria-label='Design changes'>
        <div className='export-design-block-h'>
          <div>
            <div className='export-design-block-kicker'>Design changes</div>
            <h3 className='export-design-block-title'>{view.title}</h3>
            <div className='export-design-block-sub'>{view.sub}</div>
          </div>
          <div className='export-design-block-pills'>
            <span className={`export-design-pill ${view.pillClass}`}>{view.pill}</span>
          </div>
        </div>

        <div className='export-design-route' aria-label='From where it is to where it would land'>
          <div className='export-design-route-node'>
            <div className='t'>Now</div>
            <div className='v'>{placeLabelOf(card.from)}</div>
          </div>
          <div className='export-design-route-arrow' aria-hidden='true'>
            →
          </div>
          <div className={`export-design-route-node ${d.reachable ? 'is-reachable' : 'is-unreachable'}`}>
            <div className='t'>{d.reachable ? 'After the change' : 'Target'}</div>
            <div className='v'>{view.after}</div>
          </div>
        </div>

        {d.reachable ? (
          <>
            {d.changes.length > 0 && (
              <section>
                <div className='export-design-section-h'>
                  {d.changes.length === 1 ? 'The change' : 'The changes'}
                </div>
                <div className='export-design-changes'>
                  {d.changes.map((change, index) => {
                    const name = changeNameOf(change);
                    const from = cardText(change.current_value);
                    const to = cardText(change.proposed_value);
                    const citation = cardText(change.constraint_citation);
                    const rationale = cardText(change.rationale);
                    const feasibility = cardText(change.feasibility);
                    return (
                      <div key={`${citation}-${index}`} className='export-design-change'>
                        <div className='export-design-change-t'>{cardText(change.description) || name}</div>
                        {(from || to) && (
                          <div className={`export-design-fromto${view.towardMore ? ' is-avoid' : ''}`}>
                            {name && <span className='export-design-fromto-name'>{name}:</span>}
                            {from && <s>{from}</s>}
                            {from && to && <span aria-hidden='true'>→</span>}
                            {to && <span className='to'>{to}</span>}
                          </div>
                        )}
                        {citation && (
                          <div className='export-design-change-d'>
                            {view.towardMore ? 'Would meet' : 'Clears'} <b>{citation}</b>
                            {rationale ? `: ${rationale}` : '.'}
                          </div>
                        )}
                        {feasibility && (
                          <div className='export-design-cost'>
                            {view.towardMore ? 'Note' : 'Trade-off'}: {feasibility}
                          </div>
                        )}
                        {change.destroys_capability && (
                          <div className='export-design-cost is-warn'>
                            This breaks a capability you said the product must keep.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            {impact && (
              <div className={`export-design-inset export-design-inset-text${view.tradeOff ? ' export-design-impact-warn' : ''}`}>
                <b>{view.towardMore ? 'What it would become:' : 'What it costs:'}</b> {impact}
              </div>
            )}
            {!view.towardMore && stillToCheck}
            {/* QA-4: "Test this change" is in the block's second step, after Continue. */}
            <div className='export-design-change-hint'>
              A design change doesn&apos;t change the classification until the changed product is classified. To
              re-classify with {d.changes.length > 1 ? 'them' : 'it'}, choose <b>Continue</b> below, then{' '}
              <b>{d.changes.length > 1 ? 'Test these changes' : 'Test this change'}</b>.
            </div>
          </>
        ) : (
          <>
            <section>
              <div className='export-design-section-h'>Why</div>
              <div className='export-design-inset export-design-inset-text export-design-screen-reasons'>
                {cardText(d.infeasible_reason) && <div>{cardText(d.infeasible_reason)}</div>}
                {d.residual_constraints.map((r) => (
                  <div key={r}>
                    <b>Still binding:</b> {cardText(r)}
                  </div>
                ))}
              </div>
            </section>
            {!view.towardMore && (
            <section>
              <div className='export-design-section-h'>What you can do instead</div>
              <details className='export-design-more export-design-explainer'>
                <summary>
                  {card.from.regime === 'ITAR'
                    ? 'Learn about a Commodity Jurisdiction request'
                    : 'Learn about a commodity classification request (CCATS)'}
                </summary>
                <div className='export-design-explainer-text'>
                  {card.from.regime === 'ITAR' ? CJ_EXPLAINER : CCATS_EXPLAINER}
                </div>
              </details>
              <div className='export-design-change-hint'>
                Save this result below to keep it for a professional review.
              </div>
            </section>
            )}
          </>
        )}

        {explanation && (
          <details className='export-design-more'>
            <summary>Full reasoning</summary>
            <div className='export-design-explanation'>{explanation}</div>
          </details>
        )}
      </article>
    </div>
  );
};

export default ExportDesignChangePathCard;
